import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { ErrorState, LoadingState } from '../../components/ui/Feedback';
import { PrintLetterHead, useLetterHead } from '../../components/ui/PrintLetterHead';
import { useStoreRequisition } from './store.queries';

const dash = (value: unknown): string => (value === null || value === undefined || value === '' ? '-' : String(value));
const REQUISITION_STATUS_LABELS: Record<string, string> = { submitted: 'Pending', verified: 'Verified', approved: 'Approved', 'partially-issued': 'Partially Issued', fulfilled: 'Fulfilled', rejected: 'Rejected', cancelled: 'Cancelled' };
const requisitionStatusLabel = (value: unknown): string => REQUISITION_STATUS_LABELS[String(value)] ?? dash(value);
const dateTimeFormatter = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
const formatPrintDateTime = (value: unknown): string => {
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return '-';
  const parts = Object.fromEntries(dateTimeFormatter.formatToParts(date).map((part) => [part.type, part.value]));
  return `${parts.day}-${parts.month}-${parts.year} ${parts.hour}:${parts.minute} ${(parts.dayPeriod ?? '').toUpperCase()}`;
};
const formatMoney = (value: number): string => value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const asRecord = (value: unknown): Record<string, unknown> | null => (value && typeof value === 'object' ? value as Record<string, unknown> : null);

export const StoreRequisitionPrintPage = () => {
  const { id } = useParams();
  const letterHead = useLetterHead();
  const query = useStoreRequisition(id ?? '');

  useEffect(() => {
    if (letterHead.isReady && query.data && query.data.status === 'approved') window.setTimeout(() => window.print(), 300);
  }, [letterHead.isReady, query.data]);

  if (query.isLoading || !letterHead.isReady) return <LoadingState />;
  if (query.isError || !query.data) return <ErrorState title="Requisition not found" message="The requisition could not be loaded." />;
  if (query.data.status !== 'approved') return <ErrorState title="Not available for print" message="Only an approved requisition can be printed." />;

  const row = query.data;
  const department = asRecord(row.department);
  const patient = asRecord(row.patient);
  const generatedBy = asRecord(row.generatedBy);
  const verifiedBy = asRecord(row.verifiedBy);
  const approvedBy = asRecord(row.approvedBy);
  const lines = Array.isArray(row.lines) ? row.lines as Record<string, unknown>[] : [];

  const lineRows = lines.map((line, index) => {
    const itemRef = asRecord(line.item);
    const unit = asRecord(itemRef?.unit);
    const subUnit = asRecord(itemRef?.subUnit);
    const unitName = String(line.unitName ?? unit?.name ?? '').trim();
    const subUnitName = String(line.subUnitName ?? subUnit?.name ?? '').trim();
    const subUnitsPerUnit = Number(line.subUnitsPerUnit ?? itemRef?.subUnitsPerUnit ?? 1) || 1;
    const unitQuantity = Number(line.unitQuantity ?? 0);
    const subUnitQuantity = Number(line.subUnitQuantity ?? 0);
    const requestedQuantity = Number(line.requestedQuantity ?? 0);
    const rate = Number(line.rate ?? 0);
    const totalPrice = (requestedQuantity / subUnitsPerUnit) * rate;
    return { key: dash(line._id ?? index), index, itemName: dash(line.itemName), unitName, subUnitName, subUnitsPerUnit, unitQuantity, subUnitQuantity, requestedQuantity, rate, totalPrice };
  });
  const grandTotal = lineRows.reduce((sum, line) => sum + line.totalPrice, 0);

  return (
    <div className="print-requisition-page">
      <div className="print-requisition-toolbar no-print">
        <button type="button" onClick={() => window.print()}>Print</button>
        <button type="button" className="danger" onClick={() => window.close()}>Close</button>
      </div>

      <PrintLetterHead imageUrl={letterHead.imageUrl} />
      <div className="print-requisition-header">
        <h2>Store Requisition</h2>
      </div>

      <table className="print-requisition-header-table"><tbody>
        <tr>
          <td><strong>Requisition No :</strong> {dash(row.requisitionNumber)}</td>
          <td><strong>Requisition Date :</strong> {formatPrintDateTime(row.requisitionDate)}</td>
          <td><strong>Department :</strong> <span className="print-requisition-link">{row.requisitionFor === 'patient' ? dash(row.patientName ?? patient?.name ?? patient?.medicalRecordNumber) : dash(department?.name ?? department?.departmentName)}</span></td>
        </tr>
        <tr>
          <td><strong>Generated By :</strong> {dash(generatedBy?.name)}</td>
          <td><strong>Verified By :</strong> {dash(verifiedBy?.name)}</td>
          <td><strong>Approved By :</strong> {dash(approvedBy?.name)}</td>
        </tr>
        <tr>
          <td><strong>Status :</strong> <span className="print-requisition-status">{requisitionStatusLabel(row.status)}</span></td>
          <td colSpan={2}><strong>Note :</strong> {row.note ? dash(row.note) : ''}</td>
        </tr>
      </tbody></table>

      <table className="print-requisition-items">
        <thead><tr><th>#</th><th>Item Name</th><th>Unit</th><th>Sub-Unit</th><th>Total</th><th>Unit Price</th><th>Total Price</th></tr></thead>
        <tbody>
          {lineRows.map((line) => (
            <tr key={line.key}>
              <td>{line.index + 1}</td>
              <td>{line.itemName}</td>
              <td>{line.unitQuantity}{line.unitName ? ` ${line.unitName}` : ''}</td>
              <td>{line.subUnitQuantity}{line.subUnitName ? ` ${line.subUnitName}` : ''}</td>
              <td>
                {line.requestedQuantity}{line.subUnitName || line.unitName ? ` ${line.subUnitName || line.unitName}` : ''}
                {line.unitName && line.subUnitName ? <small>(1 {line.unitName} = {line.subUnitsPerUnit} {line.subUnitName})</small> : null}
              </td>
              <td>{formatMoney(line.rate)}</td>
              <td>{formatMoney(line.totalPrice)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot><tr><th colSpan={6} className="print-requisition-total-label">Grand Total</th><th>{formatMoney(grandTotal)}</th></tr></tfoot>
      </table>
    </div>
  );
};
